import { Link, createFileRoute } from '@tanstack/react-router'
import { ArrowLeft, History, Layers } from 'lucide-react'

import { agriInReasonLabel } from '../stock-in-notes/index'
import { agriOutReasonLabel } from '../stock-out-notes/index'
import type { AgriMaterialLotHistoryEntry } from '@/lib/api/agri-material-lot-api'
import { PageBackButton } from '@/components/common/PageBackButton'
import { agriUnitLabel } from '@/components/agricultural-materials/agri-unit-label'
import { HistoryEntryCard } from '@/components/common/HistoryEntryCard'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { WarehouseNoteStatusBadge } from '@/components/warehouse/note-status-badge'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {
    useAgriLot,
    useAgriLotHistory,
} from '@/hooks/use-agri-material-lot-query'
import { formatDateTimeVN, formatDateVN, formatQty } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/agricultural-materials/lots/$id',
)({
    component: AgriLotDetailPage,
})

function AgriLotDetailPage() {
    const { id } = Route.useParams()
    const { data: lotRes, isLoading } = useAgriLot(id)
    const historyQuery = useAgriLotHistory(id)
    const lot = lotRes?.data
    const history = historyQuery.data?.pages.flatMap((p) => p.data ?? []) ?? []

    if (isLoading) return <div className="p-6">{m.aml_loading()}</div>
    if (!lot) return <div className="p-6">{m.aml_lot_not_found()}</div>

    const unit = agriUnitLabel(lot.material_unit)

    return (
        <div className="space-y-4 page-pad">
            <Card>
                <CardHeader>
                    <div className="flex items-center gap-2">
                        <PageBackButton asChild>
                            <Link to="/agricultural-materials/lots">
                                <ArrowLeft className="size-4" />
                            </Link>
                        </PageBackButton>
                        <Layers className="size-5" />
                        <CardTitle className="font-mono text-base md:text-2xl">
                            {lot.code}
                        </CardTitle>
                    </div>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-3">
                    <div className="col-span-2">
                        <Field
                            label={m.aml_lots_col_material()}
                            value={lot.material_name}
                        />
                    </div>
                    <Field
                        label={m.aml_lots_col_warehouse()}
                        value={lot.warehouse_name}
                    />
                    <Field
                        label={m.aml_lots_col_created()}
                        value={formatDateTimeVN(lot.created_at)}
                    />
                    <Field
                        label={m.aml_lot_supplier()}
                        value={lot.supplier_name || '—'}
                    />
                    <Field
                        label={m.aml_lot_mfg()}
                        value={
                            lot.manufactured_date
                                ? formatDateVN(lot.manufactured_date)
                                : '—'
                        }
                    />
                    <Field
                        label={m.aml_lots_col_exp()}
                        value={
                            lot.expiry_date
                                ? formatDateVN(lot.expiry_date)
                                : '—'
                        }
                    />
                    <div className="col-span-2 rounded-md bg-primary/10 p-3 text-center">
                        <p className="text-xs text-muted-foreground">
                            {m.aml_lots_col_available()}
                        </p>
                        <p className="text-2xl font-semibold tabular-nums text-primary md:text-xl">
                            {formatQty(lot.quantity_available)}
                            {unit ? ` ${unit}` : ''}
                        </p>
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <div className="flex items-center gap-2">
                        <History className="size-5" />
                        <CardTitle>{m.aml_lot_history_title()}</CardTitle>
                    </div>
                </CardHeader>
                <CardContent>
                    {history.length === 0 ? (
                        <p className="py-2 text-sm text-muted-foreground">
                            {m.aml_history_empty()}
                        </p>
                    ) : (
                        <>
                            {/* Mobile: card list */}
                            <div className="space-y-2 md:hidden">
                                {history.map((h, idx) => (
                                    <HistoryEntryCard
                                        key={`${h.note_id}-${idx}`}
                                        type={h.note_type}
                                        createdAt={h.created_at}
                                        noteLink={<NoteLink entry={h} />}
                                        statusBadge={
                                            <WarehouseNoteStatusBadge
                                                status={h.status}
                                            />
                                        }
                                        reason={reasonLabel(h)}
                                        notes={h.notes}
                                        quantity={h.quantity}
                                        unit={unit}
                                        createdBy={h.created_by_name}
                                    />
                                ))}
                            </div>

                            {/* Desktop: table */}
                            <div className="hidden overflow-x-auto rounded-md border md:block">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>
                                                {m.sl_lot_history_col_date()}
                                            </TableHead>
                                            <TableHead>
                                                {m.sl_lot_history_col_type()}
                                            </TableHead>
                                            <TableHead>
                                                {m.sl_lot_history_col_note()}
                                            </TableHead>
                                            <TableHead>
                                                {m.sl_lot_history_col_status()}
                                            </TableHead>
                                            <TableHead>
                                                {m.sl_lot_history_col_reason()}
                                            </TableHead>
                                            <TableHead>
                                                {m.sl_lot_history_col_notes()}
                                            </TableHead>
                                            <TableHead className="text-right">
                                                {m.sl_lot_history_col_quantity()}
                                            </TableHead>
                                            <TableHead>
                                                {m.sl_lot_history_col_created_by()}
                                            </TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {history.map((h, idx) => (
                                            <TableRow
                                                key={`${h.note_id}-${idx}`}
                                            >
                                                <TableCell className="text-sm text-muted-foreground">
                                                    {formatDateTimeVN(
                                                        h.created_at,
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <TypeBadge
                                                        type={h.note_type}
                                                    />
                                                </TableCell>
                                                <TableCell className="font-mono text-sm">
                                                    <NoteLink entry={h} />
                                                </TableCell>
                                                <TableCell>
                                                    <WarehouseNoteStatusBadge
                                                        status={h.status}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {reasonLabel(h)}
                                                </TableCell>
                                                <TableCell className="max-w-xs truncate text-sm text-muted-foreground">
                                                    {h.notes ?? '—'}
                                                </TableCell>
                                                <TableCell className="text-right tabular-nums">
                                                    {h.note_type === 'in'
                                                        ? '+'
                                                        : '−'}
                                                    {formatQty(h.quantity)}
                                                </TableCell>
                                                <TableCell>
                                                    {h.created_by_name ?? '—'}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </>
                    )}
                    {historyQuery.hasNextPage && (
                        <Button
                            variant="outline"
                            className="mt-3 h-11 w-full"
                            disabled={historyQuery.isFetchingNextPage}
                            onClick={() => void historyQuery.fetchNextPage()}
                        >
                            {historyQuery.isFetchingNextPage
                                ? m.list_loading_more()
                                : m.list_load_more()}
                        </Button>
                    )}
                </CardContent>
            </Card>
        </div>
    )
}

function TypeBadge({ type }: { type: 'in' | 'out' }) {
    if (type === 'in') {
        return (
            <Badge className="bg-blue-100 text-blue-700">
                {m.sl_lot_history_type_in()}
            </Badge>
        )
    }
    return (
        <Badge className="bg-orange-100 text-orange-700">
            {m.sl_lot_history_type_out()}
        </Badge>
    )
}

function NoteLink({ entry }: { entry: AgriMaterialLotHistoryEntry }) {
    if (entry.note_type === 'in') {
        return (
            <Link
                to="/agricultural-materials/stock-in-notes/$id"
                params={{ id: entry.note_id }}
                className="text-primary hover:underline"
            >
                {entry.note_code}
            </Link>
        )
    }
    return (
        <Link
            to="/agricultural-materials/stock-out-notes/$id"
            params={{ id: entry.note_id }}
            className="text-primary hover:underline"
        >
            {entry.note_code}
        </Link>
    )
}

function reasonLabel(h: AgriMaterialLotHistoryEntry): string {
    return h.note_type === 'in'
        ? agriInReasonLabel(h.reason)
        : agriOutReasonLabel(h.reason)
}

function Field({ label, value }: { label: string; value?: string }) {
    return (
        <div>
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className="text-base font-medium md:text-sm">{value ?? '—'}</p>
        </div>
    )
}
